import { defineStore } from 'pinia'

// VIP套餐接口
export interface VipPackage {
  productId: string           // 产品ID,例如 'vip_month', 'vip_quarter', 'vip_year'
  name: string               // 展示主标题（来自后端 up_section_text）
  price: number              // 价格(单位:元)
  originalPrice?: number     // 原价(用于显示折扣),可选（从 lower_section 文案解析）
  tagText?: string          // 角标文案（来自后端 title_tag.title_tag_text）
  subtitle?: string         // 副标题（来自后端 lower_section.lower_section_text）
  footerText?: string       // 底部文案（来自后端 footer.footer_text）
  footerStyle?: number      // 底部样式（来自后端 footer.footer_style）
}

// 页数包接口
export interface PagePackage {
  productId: string          // 产品ID,例如 'pages_100', 'pages_500'
  name: string              // 套餐名称,例如 '100页', '500页'
  pages: number             // 页数
  price: number             // 价格(单位:元)
  originalPrice?: number     // 原价(用于显示折扣),可选
  unitPrice: number         // 单价(每页价格)
  tagText?: string          // 角标文案（来自后端 title_tag.title_tag_text）
  subtitle?: string         // 副标题（来自后端 lower_section.lower_section_text）
  footerText?: string       // 底部文案（来自后端 footer.footer_text）
  footerStyle?: number      // 底部样式（来自后端 footer.footer_style）
}

// 支付方式接口
export interface PaymentMethod {
  method: 'wechat' | 'alipay'  // 支付方式
  icon: string                  // 图标路径
  label: string                 // 显示名称
  isDefault: boolean            // 是否默认选中
}

// 支付状态定义 (对齐后端 payv2dto.OrderStatus + 前端扩展)
// 后端状态: "paid" | "paying" | "fail" | "refund" | ""
// 前端扩展: "none" (未开始) | "expired" (轮询超时)
export type PaymentStatus = 
  | 'none'      // 初始状态
  | 'paying'    // 支付中 (对应后端 Paying / un_paid)
  | 'paid'      // 已支付 (对应后端 Paid)
  | 'fail'      // 支付失败 (对应后端 Fail)
  | 'refund'    // 已退款 (对应后端 Refund)
  | 'expired'   // 轮询超时 (前端逻辑)

// Store状态接口
interface PayState {
  vipPackages: VipPackage[]
  pagePackages: PagePackage[]
  selectedPackage: VipPackage | PagePackage | null
  paymentMethods: PaymentMethod[]
  selectedPaymentMethod: PaymentMethod
  isLoading: boolean
  
  // 支付状态
  qrCodeUrl: string | null
  currentOrderId: string | null
  paymentStatus: PaymentStatus
}

export const usePayStore = defineStore('pay', {
  state: (): PayState => ({
    // VIP套餐列表
    vipPackages: [],

    // 页数包列表
    pagePackages: [],

    // 当前选中的套餐
    selectedPackage: null,

    // 支付方式列表 (仅微信)
    paymentMethods: [
      { method: 'wechat', icon: '/icons/wechat-pay.png', label: '微信支付', isDefault: true }
    ],

    // 当前选中的支付方式
    selectedPaymentMethod: { method: 'wechat', icon: '/icons/wechat-pay.png', label: '微信支付', isDefault: true },

    // 加载状态
    isLoading: false,
    
    // 支付相关
    qrCodeUrl: null,
    currentOrderId: null,
    paymentStatus: 'none'
  }),

  getters: {
    // 是否可以购买(是否已选择套餐)
    canPurchase: (state) => state.selectedPackage !== null,

    // 显示价格（仅在已选择套餐时返回，避免中文硬编码泄露到英文界面）
    displayPrice: (state) => {
      if (!state.selectedPackage) return ''
      return `¥${state.selectedPackage.price}`
    }
  },

  actions: {
    // 获取VIP套餐列表
    async fetchVipPackages() {
      this.isLoading = true
      try {
        const { fetchVipPackages } = usePayApi()
        const result = await fetchVipPackages()
        this.vipPackages = result.packages

        // 优先选中后端给出的默认产品ID；其次推荐；否则第一个
        const byDefault = result.defaultProductId
          ? this.vipPackages.find(pkg => pkg.productId === result.defaultProductId)
          : null
        this.selectedPackage = byDefault || this.vipPackages[0] || null
      } catch (error) {
        console.error('获取VIP套餐失败:', error)
      } finally {
        this.isLoading = false
      }
    },

    // 获取页数包列表
    async fetchPagePackages() {
      this.isLoading = true
      try {
        const { fetchPagePackages } = usePayApi()
        const result = await fetchPagePackages()
        this.pagePackages = result.packages
      } catch (error) {
        console.error('获取页数包失败:', error)
      } finally {
        this.isLoading = false
      }
    },

    // 设置选中的套餐
    setSelectedPackage(pkg: VipPackage | PagePackage) {
      this.selectedPackage = pkg
    },

    // 设置支付方式
    setPaymentMethod(method: PaymentMethod) {
      this.selectedPaymentMethod = method
    },

    // 重置支付状态
    resetPaymentState() {
      this.qrCodeUrl = null
      this.currentOrderId = null
      this.paymentStatus = 'none'
    },

    // 创建支付订单
    async createOrder(pkg: VipPackage | PagePackage, method: PaymentMethod): Promise<{ orderId: string }> {
      const userStore = useUserStore()
      if (!userStore.isLoggedIn) {
        userStore.setAuthModalVisible(true)
        throw new Error('请先登录后再购买')
      }

      this.resetPaymentState()
      this.isLoading = true
      
      try {
        const { createPaymentOrder } = usePayApi()
        const { orderId, codeUrl } = await createPaymentOrder({
          productId: pkg.productId,
          paymentMethod: method.method
        })

        this.currentOrderId = orderId
        this.qrCodeUrl = codeUrl || null
        this.paymentStatus = 'paying'
        
        // 启动轮询
        this.pollPaymentStatus()
        
        return { orderId }
      } catch (error) {
        console.error('创建订单失败:', error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    // 轮询支付状态
    async pollPaymentStatus() {
      const orderId = this.currentOrderId
      if (!orderId) return

      const { checkPaymentStatus } = usePayApi()
      const startTime = Date.now()
      const timeout = 5 * 60 * 1000 // 5分钟超时

      // 循环检查
      // 注意：'paying' 和 'none' 都可能是中间状态，这里主要判断是否还是 paying
      while (this.paymentStatus === 'paying' && this.currentOrderId === orderId) {
        // 超时检查
        if (Date.now() - startTime > timeout) {
          this.paymentStatus = 'expired'
          break
        }

        try {
          const { status } = await checkPaymentStatus(orderId)
          
          if (status === 'paid') {
            this.paymentStatus = 'paid'
            await this.onPaySuccess(orderId)
            return
          } 
          
          if (status === 'fail') {
            this.paymentStatus = 'fail'
            return
          }

          // 其他状态如 'paying' 或 '' 继续轮询
        } catch (e) {
          console.error('Poll status error:', e)
        }

        // 间隔2秒
        await new Promise(resolve => setTimeout(resolve, 2000))
      }
    },

    async onPaySuccess(orderId: string) {
      const myPage = useMyPageStore()
      const userStore = useUserStore()
      try {
        await Promise.all([
          userStore.fetchUserInfoFromUserMix(),
          myPage.fetchConsumptionRecords()
        ])
      } catch (e) {
        // 忽略刷新失败，由全局拦截器处理
      }
      
      // 弹窗模式下，通常由UI监听状态变更来跳转或显示成功，
      // 但为了保险，这里不直接跳转，而是让UI层处理跳转
    },

    // 清空选中的套餐
    clearSelection() {
      this.selectedPackage = null
    }
  }
})
